import { Spin, Tabs, Descriptions, Empty, Table, Tag, Button, List, Modal, Typography } from "antd";
import { FC, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Clock,
  Bell,
  Calendar,
  Link2,
  UserCheck,
  Receipt,
  FileText,
  Download,
  Eye,
  Upload,
} from "lucide-react";
import dayjs from "dayjs";
import { useAuth } from "~/store";
import { capitalize, getClientName } from "~/utils";
import { ClientStatus, SortOrder } from "~/utils/constants";
import { TClient, TFile } from "~/types";
import { useStorageInvoices } from "~/services/storage/storage.queries";
import { StorageInvoice, StorageInvoiceStatus } from "~/services/storage/storage.api";
import { useGetMyFiles } from "~/services/file";
import { useNotification } from "~/hooks";
import { useGetFileMimetype } from "~/services/common";
import { ProfileDocumentUpload } from "./profile-document-upload";
import { useGetSubClients } from "~/services/client/client.queries";

const { Text } = Typography;

export const ClientProfile: FC = () => {
  const { user } = useAuth();
  const client: TClient | null | undefined = user?.client;
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>("overview");

  // Sync tab with URL params
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (key === "overview") {
        newParams.delete("tab");
      } else {
        newParams.set("tab", key);
      }
      return newParams;
    });
  };

  const formatAddress = (address: any) => {
    if (!address) return "Not provided";
    const parts = [
      address.address,
      address.apt,
      address.city,
      address.state,
      address.zip,
      address.country,
    ].filter(Boolean);
    return parts.join(", ") || "Not provided";
  };

  const formatTime = (time: Date | string | undefined) => {
    if (!time) return null;
    return dayjs(time).format("h:mm A");
  };

  const tabItems = [
    {
      key: "overview",
      label: (
        <span className="flex items-center gap-2">
          <Building2 size={14} />
          Overview
        </span>
      ),
      children: client && <OverviewTab client={client} formatAddress={formatAddress} />,
    },
    {
      key: "addresses",
      label: (
        <span className="flex items-center gap-2">
          <MapPin size={14} />
          Addresses
        </span>
      ),
      children: client && <AddressesTab client={client} formatAddress={formatAddress} />,
    },
    {
      key: "rates",
      label: (
        <span className="flex items-center gap-2">
          <DollarSign size={14} />
          Rates & Billing
        </span>
      ),
      children: client && <RatesTab client={client} />,
    },
    {
      key: "preferences",
      label: (
        <span className="flex items-center gap-2">
          <Bell size={14} />
          Preferences
        </span>
      ),
      children: client && <PreferencesTab client={client} formatTime={formatTime} />,
    },
    {
      key: "documents",
      label: (
        <span className="flex items-center gap-2">
          <FileText size={14} />
          Documents
        </span>
      ),
      children: client?.id && <DocumentsTab clientId={client.id} />,
    },
    {
      key: "storage-invoices",
      label: (
        <span className="flex items-center gap-2">
          <Receipt size={14} />
          Storage Invoices
        </span>
      ),
      children: client?.id && <StorageInvoicesTab clientId={client.id} />,
    },
  ];

  if (!client) {
    return (
      <div className="p-6">
        <div className="bg-[#fffff5] border-2 border-black p-8 text-center">
          <Empty description="No client information available" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Title - Neo Brutalist */}
      <div className="mb-6">
        <div className="border-2 border-black inline-block px-4 py-2 mb-2 bg-[#fffff5]">
          <h1 className="text-2xl sm:text-3xl font-bold text-black uppercase tracking-wide !mb-0">
            Profile
          </h1>
        </div>
        <p className="text-black font-medium">
          {client.companyName || "Client Profile"}
          {client.clientRef && ` (${client.clientRef})`}
        </p>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {client.type && (
          <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black bg-[#fffff5] text-black">
            {capitalize(client.type)}
          </span>
        )}
        {client.status && (
          <span className={`inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black ${
            client.status === ClientStatus.ACTIVE
              ? "bg-black text-white"
              : "bg-[#fffff5] text-black"
          }`}>
            {capitalize(client.status)}
          </span>
        )}
        {client.isSubClient && (
          <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black bg-[#fffff5] text-black">
            Subclient
          </span>
        )}
        <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black bg-black text-white flex items-center gap-1">
          <UserCheck size={11} />
          User Account
        </span>
      </div>

      {/* Content - Neo Brutalist */}
      <div className="border-2 border-black bg-[#fffff5]">
        <div className="p-4 sm:p-6">
          <Tabs 
            items={tabItems} 
            className="client-profile-tabs"
            activeKey={activeTab}
            onChange={handleTabChange}
          />
        </div>
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab: FC<{ client: TClient; formatAddress: (addr: any) => string }> = ({ client, formatAddress }) => {
  // Fetch subclients if this client is not a subclient
  const { data: subClientsData, isLoading: subClientsLoading } = useGetSubClients(
    client?.id || "",
    {
      enabled: !!client?.id && !client?.isSubClient,
    }
  );

  const subClients = subClientsData?.items || [];

  return (
    <div className="space-y-6">
      {/* Company Info */}
      <div className="border-2 border-black bg-[#fffff5] p-4">
        <div className="border-2 border-black inline-block px-3 py-1 mb-3">
          <h3 className="font-bold text-sm uppercase tracking-wide text-black !mb-0 flex items-center gap-2">
            <Building2 size={14} />
            Company Information
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Company Name</div>
            <div className="font-medium">{client.companyName || "-"}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Client Ref</div>
            <div className="font-medium">{client.clientRef || "-"}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Type</div>
            <div className="font-medium">{client.type ? capitalize(client.type) : "-"}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Status</div>
            <div>
              {client.status ? (
                <span className={`inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black ${
                  client.status === ClientStatus.ACTIVE
                    ? "bg-black text-white"
                    : "bg-[#fffff5] text-black"
                }`}>
                  {capitalize(client.status)}
                </span>
              ) : "-"}
            </div>
          </div>
        </div>
      </div>

      {/* Primary Contact */}
      <div className="border-2 border-black bg-[#fffff5] p-4">
        <div className="border-2 border-black inline-block px-3 py-1 mb-3">
          <h3 className="font-bold text-sm uppercase tracking-wide text-black !mb-0 flex items-center gap-2">
            <User size={14} />
            Primary Contact
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Name</div>
            <div className="font-medium">{getClientName(client) || "-"}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Email</div>
            <div className="font-medium">
              {client.email ? (
                <a href={`mailto:${client.email}`} className="text-black hover:underline font-bold">
                  {client.email}
                </a>
              ) : "-"}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Phone</div>
            <div className="font-medium">
              {client.contactPhone ? (
                <a href={`tel:${client.contactPhone}`} className="text-black hover:underline font-bold">
                  {client.contactPhone}
                </a>
              ) : "-"}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Company Direct</div>
            <div className="font-medium">{client.companyDirectPhone || "-"}</div>
          </div>
          <div className="sm:col-span-2">
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Company Mobile</div>
            <div className="font-medium">{client.companyMobile || "-"}</div>
          </div>
        </div>
      </div>

      {/* User Account Status */}
      <div className="border-2 border-black bg-[#fffff5] p-4">
        <div className="border-2 border-black inline-block px-3 py-1 mb-3">
          <h3 className="font-bold text-sm uppercase tracking-wide text-black !mb-0 flex items-center gap-2">
            <UserCheck size={14} />
            User Account
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Status</div>
            <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black bg-black text-white">
              Active
            </span>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">User Email</div>
            <div className="font-medium">{client.user?.email || client.email || "-"}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Role</div>
            <div className="font-medium">{client.user?.role ? capitalize(client.user.role) : "Client"}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Portal Access</div>
            <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black bg-black text-white">
              Enabled
            </span>
          </div>
        </div>
      </div>

      {/* Subclients Table - Only show if client is not a subclient */}
      {!client.isSubClient && (
        <div className="border-2 border-black bg-[#fffff5] p-4">
          <div className="border-2 border-black inline-block px-3 py-1 mb-3">
            <h3 className="font-bold text-sm uppercase tracking-wide text-black !mb-0 flex items-center gap-2">
              <Link2 size={14} />
              Subclients ({subClientsLoading ? "..." : subClients.length})
            </h3>
          </div>
          {subClientsLoading ? (
            <div className="flex justify-center py-8">
              <Spin />
            </div>
          ) : subClients.length > 0 ? (
            <Table
              dataSource={subClients}
              rowKey="id"
              size="small"
              pagination={false}
              className="neo-brutalist-table border-2 border-black"
              onRow={() => ({
                className: "border-b-2 border-black hover:bg-[#fffef5]",
              })}
              columns={[
                {
                  title: (
                    <span className="font-bold uppercase tracking-wide text-black">Name</span>
                  ),
                  key: "name",
                  render: (_: any, subClient: TClient) => (
                    <div className="font-bold text-black">
                      {subClient.companyName || 
                       `${subClient.firstName || ""} ${subClient.lastName || ""}`.trim() || 
                       subClient.clientRef || 
                       "Unnamed"}
                    </div>
                  ),
                },
                {
                  title: (
                    <span className="font-bold uppercase tracking-wide text-black">Client Ref</span>
                  ),
                  dataIndex: "clientRef",
                  key: "clientRef",
                  render: (ref: string) => (
                    <span className="font-medium text-black">{ref || "-"}</span>
                  ),
                },
                {
                  title: (
                    <span className="font-bold uppercase tracking-wide text-black">Email</span>
                  ),
                  dataIndex: "email",
                  key: "email",
                  render: (email: string) => (
                    <span className="font-medium text-black">{email || "-"}</span>
                  ),
                },
                {
                  title: (
                    <span className="font-bold uppercase tracking-wide text-black">Status</span>
                  ),
                  dataIndex: "status",
                  key: "status",
                  render: (status: ClientStatus) => (
                    <span className={`inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black ${
                      status === ClientStatus.ACTIVE
                        ? "bg-black text-white"
                        : "bg-[#fffff5] text-black"
                    }`}>
                      {capitalize(status)}
                    </span>
                  ),
                },
              ]}
            />
          ) : (
            <div className="text-black text-sm border-2 border-black p-3 bg-[#fffff5] font-medium text-center">
              No subclients
            </div>
          )}
        </div>
      )}

      {/* Client Relationship */}
      {client.isSubClient && (
        <div className="border-2 border-black bg-[#fffff5] p-4">
          <div className="border-2 border-black inline-block px-3 py-1 mb-3">
            <h3 className="font-bold text-sm uppercase tracking-wide text-black !mb-0 flex items-center gap-2">
              <Link2 size={14} />
              Client Relationship
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wide mb-1">Type</div>
              <div className="font-medium">Subclient</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide mb-1">Parent Client</div>
              <div className="font-medium">{client.parentClient?.companyName || client.parentClientId || "-"}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide mb-1">Billing to Parent</div>
              <div className="font-medium">{client.isBillingParent ? "Yes" : "No"}</div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="border-2 border-black bg-[#fffff5] p-4 text-center">
          <div className="text-2xl font-bold text-black">{client.jobTickets?.length || 0}</div>
          <div className="text-xs font-bold uppercase tracking-wide text-black mt-1">Bookings</div>
        </div>
        <div className="border-2 border-black bg-[#fffff5] p-4 text-center">
          <div className="text-2xl font-bold text-black">{client.shippingAddresses?.length || 0}</div>
          <div className="text-xs font-bold uppercase tracking-wide text-black mt-1">Locations</div>
        </div>
      </div>
    </div>
  );
};

// Addresses Tab
const AddressesTab: FC<{ client: TClient; formatAddress: (addr: any) => string }> = ({ client, formatAddress }) => {
  return (
    <div className="space-y-6">
      {/* Shipping Addresses */}
      <div className="border-2 border-black bg-[#fffff5] p-4">
        <div className="border-2 border-black inline-block px-3 py-1 mb-3">
          <h3 className="font-bold text-sm uppercase tracking-wide text-black !mb-0 flex items-center gap-2">
            <MapPin size={14} />
            Shipping Addresses ({client.shippingAddresses?.length || 0})
          </h3>
        </div>
        {client.shippingAddresses && client.shippingAddresses.length > 0 ? (
          <div className="space-y-3">
            {client.shippingAddresses.map((addr, idx) => (
              <div key={idx} className="border-2 border-black bg-[#fffff5] p-3">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-bold text-sm uppercase text-black">
                    {addr.addressName || `Address ${idx + 1}`}
                    {idx === 0 && <span className="ml-2 text-xs font-normal normal-case">(Primary)</span>}
                  </div>
                  {addr.travelTime && (
                    <span className="text-xs font-bold uppercase border-2 border-black px-2 py-1 bg-black text-white">
                      {addr.travelTime} min
                    </span>
                  )}
                </div>
                <div className="text-sm font-medium text-black">
                  <div>{addr.address}</div>
                  {addr.apt && <div>{addr.apt}</div>}
                  <div>{[addr.city, addr.state, addr.zip].filter(Boolean).join(", ")}</div>
                  {addr.country && <div>{addr.country}</div>}
                </div>
                {addr.specialInstructions && (
                  <div className="mt-3 pt-3 border-t-2 border-black text-xs font-medium text-black">
                    <span className="font-bold uppercase">Note:</span> {addr.specialInstructions}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-black text-sm border-2 border-black p-3 bg-[#fffff5] font-medium">No shipping addresses</div>
        )}
      </div>

      {/* Billing Address */}
      <div className="border-2 border-black bg-[#fffff5] p-4">
        <div className="border-2 border-black inline-block px-3 py-1 mb-3">
          <h3 className="font-bold text-sm uppercase tracking-wide text-black !mb-0 flex items-center gap-2">
            <DollarSign size={14} />
            Billing Address
          </h3>
        </div>
        {client.billingAddress ? (
          <div className="border-2 border-black bg-[#fffff5] p-3">
            <div className="font-bold text-sm uppercase text-black mb-2">
              {client.billingAddress.addressName || "Billing Address"}
            </div>
            <div className="text-sm font-medium text-black">
              <div>{client.billingAddress.address}</div>
              {client.billingAddress.apt && <div>{client.billingAddress.apt}</div>}
              <div>{[client.billingAddress.city, client.billingAddress.state, client.billingAddress.zip].filter(Boolean).join(", ")}</div>
              {client.billingAddress.country && <div>{client.billingAddress.country}</div>}
            </div>
          </div>
        ) : (
          <div className="text-black text-sm border-2 border-black p-3 bg-[#fffff5] font-medium">No billing address</div>
        )}
      </div>
    </div>
  );
};

// Rates Tab
const RatesTab: FC<{ client: TClient }> = ({ client }) => {
  const clientData = client as any; // Cast to any to access additional fields

  return (
    <div className="space-y-6">
      {/* Rate Category */}
      <div className="border-2 border-black bg-[#fffff5] p-4">
        <div className="border-2 border-black inline-block px-3 py-1 mb-3">
          <h3 className="font-bold text-sm uppercase tracking-wide text-black !mb-0 flex items-center gap-2">
            <DollarSign size={14} />
            Rate Category
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Rate Type</div>
            <div className="font-medium">{clientData.rateType ? capitalize(clientData.rateType) : "-"}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Payment Terms</div>
            <div className="font-medium">{clientData.paymentTerms ? capitalize(clientData.paymentTerms) : "-"}</div>
          </div>
        </div>
      </div>

      {/* Custom Adjustments */}
      <div className="border-2 border-black bg-[#fffff5] p-4">
        <div className="border-2 border-black inline-block px-3 py-1 mb-3">
          <h3 className="font-bold text-sm uppercase tracking-wide text-black !mb-0">
            Custom Rate Adjustments
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="border-2 border-black bg-[#fffff5] p-4 text-center">
            <div className="text-2xl font-bold text-black">
              {clientData.laborRateAdjustment || 0}%
            </div>
            <div className="text-xs font-bold uppercase tracking-wide text-black mt-1">Labor Rate</div>
          </div>
          <div className="border-2 border-black bg-[#fffff5] p-4 text-center">
            <div className="text-2xl font-bold text-black">
              {clientData.materialsAdjustment || 0}%
            </div>
            <div className="text-xs font-bold uppercase tracking-wide text-black mt-1">Materials</div>
          </div>
          <div className="border-2 border-black bg-[#fffff5] p-4 text-center">
            <div className="text-2xl font-bold text-black">
              {clientData.transportAdjustment || 0}%
            </div>
            <div className="text-xs font-bold uppercase tracking-wide text-black mt-1">Transport</div>
          </div>
        </div>
        <p className="text-xs font-medium text-black mt-3 text-center">
          0% = Standard rate • Positive = Premium • Negative = Discount
        </p>
      </div>
    </div>
  );
};

// Preferences Tab
const PreferencesTab: FC<{ client: TClient; formatTime: (time: Date | string | undefined) => string | null }> = ({ client, formatTime }) => {
  const clientData = client as any;

  return (
    <div className="space-y-6">
      {/* Communication */}
      <div className="border-2 border-black bg-[#fffff5] p-4">
        <div className="border-2 border-black inline-block px-3 py-1 mb-3">
          <h3 className="font-bold text-sm uppercase tracking-wide text-black !mb-0 flex items-center gap-2">
            <Bell size={14} />
            Communication Preferences
          </h3>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Preferred Method</div>
            <div className="font-medium">{clientData.preferredCommunicationMethod ? capitalize(clientData.preferredCommunicationMethod) : "-"}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Service Notifications</div>
            <div className="font-medium">
              {clientData.serviceNotifications?.length > 0 ? (
                <span className="text-black">
                  {clientData.serviceNotifications.join(", ")}
                </span>
              ) : "-"}
            </div>
          </div>
        </div>
      </div>

      {/* Timing Preferences */}
      <div className="border-2 border-black bg-[#fffff5] p-4">
        <div className="border-2 border-black inline-block px-3 py-1 mb-3">
          <h3 className="font-bold text-sm uppercase tracking-wide text-black !mb-0 flex items-center gap-2">
            <Calendar size={14} />
            Timing Preferences
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Preferred Days</div>
            <div className="font-medium">
              {clientData.preferredDays?.length > 0 ? (
                <span className="text-black">
                  {clientData.preferredDays.join(", ")}
                </span>
              ) : "No preference"}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-1">Time Window</div>
            <div className="font-medium">
              {client.preferredTimeWindowFrom || client.preferredTimeWindowTo ? (
                <span className="text-black">
                  {formatTime(client.preferredTimeWindowFrom) || "Any"} - {formatTime(client.preferredTimeWindowTo) || "Any"}
                </span>
              ) : "No preference"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Storage Invoices Tab
const StorageInvoicesTab: FC<{ clientId: string }> = ({ clientId }) => {
  const { data: invoicesData, isLoading } = useStorageInvoices({
    clientId,
    limit: 50,
  });

  // Filter out cancelled invoices
  const invoices = invoicesData?.invoices?.filter(
    (inv) => inv.status !== StorageInvoiceStatus.CANCELLED
  ) || [];

  const getStatusTag = (status: StorageInvoiceStatus) => {
    const config: Record<StorageInvoiceStatus, { color: string; label: string }> = {
      [StorageInvoiceStatus.DRAFT]: { color: "default", label: "Draft" },
      [StorageInvoiceStatus.SENT]: { color: "blue", label: "Sent" },
      [StorageInvoiceStatus.PAID]: { color: "green", label: "Paid" },
      [StorageInvoiceStatus.CANCELLED]: { color: "red", label: "Cancelled" },
    };
    const { color, label } = config[status];
    return <Tag color={color}>{label}</Tag>;
  };

  const columns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 200,
      render: (text: string) => (
        <span className="font-medium text-blue-600">{text}</span>
      ),
    },
    {
      title: "Period",
      key: "period",
      width: 120,
      render: (_: unknown, record: StorageInvoice) =>
        dayjs()
          .month(record.billingPeriodMonth - 1)
          .year(record.billingPeriodYear)
          .format("MMM YYYY"),
    },
    {
      title: "Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 100,
      render: (date: string) => dayjs(date).format("MM/DD/YY"),
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 100,
      align: "right" as const,
      render: (value: number | string) => (
        <span className="font-medium">${Number(value || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (status: StorageInvoiceStatus) => getStatusTag(status),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin />
      </div>
    );
  }

  if (invoices.length === 0) {
    return <Empty description="No storage invoices" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div>
      <Table
        dataSource={invoices}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ y: 300 }}
      />
      <div className="mt-3 text-xs text-gray-500 text-right">
        {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
};

// Documents Tab
const DocumentsTab: FC<{ clientId: string }> = ({ clientId }) => {
  const [selectedDoc, setSelectedDoc] = useState<TFile | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [uploadVisible, setUploadVisible] = useState(false);
  const { error } = useNotification();

  const {
    data: filesData,
    isLoading,
    refetch,
  } = useGetMyFiles({
    pageSize: 100,
    sortByCreatedAt: SortOrder.DESC,
    clientId,
  });

  // Filter to show only client documents (contracts, etc.) - files without jobTicketId
  const profileDocuments = filesData?.items?.filter(
    (file) => !file.jobTicket?.id
  ) || [];

  const viewDocument = (doc: TFile) => {
    if (!doc.filePath) {
      error({
        message: "Cannot view document",
        description: "Document URL is missing.",
      });
      return;
    }
    setSelectedDoc(doc);
    setPreviewVisible(true);
  };

  const downloadDocument = async (doc: TFile) => {
    if (!doc.filePath) {
      error({
        message: "Cannot download document",
        description: "Document URL is missing.",
      });
      return;
    }
    try {
      const response = await fetch(doc.filePath, { method: "GET" });
      if (!response.ok) throw new Error("Failed to fetch file");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.fileName || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      error({
        message: "Failed to download file",
        description: err.message,
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const DocumentPreview: FC<{ doc: TFile }> = ({ doc }) => {
    const fileUrl = `${doc.filePath}`;
    const [canLoad, setCanLoad] = useState<boolean | null>(null);
    const { data: mimeType } = useGetFileMimetype(doc.filePath);

    const canLoadFile = async (url: string) => {
      try {
        const response = await fetch(url, { method: "GET" });
        return response.ok;
      } catch (error) {
        console.error("Error checking file:", error);
        return false;
      }
    };

    useEffect(() => {
      canLoadFile(fileUrl).then((result) => setCanLoad(result));
    }, [fileUrl]);

    if (canLoad === null) {
      return <Text>Loading...</Text>;
    }

    if (!canLoad) {
      return (
        <div className="text-center p-4">
          <Text>
            Unable to load the document. The file may not exist or the server is
            not running.
          </Text>
        </div>
      );
    }

    switch (mimeType) {
      case "image/jpeg":
      case "image/png":
        return (
          <div className="text-center">
            <img
              src={fileUrl}
              alt={doc.title}
              className="max-w-full max-h-[60vh] object-contain rounded"
              onError={() => setCanLoad(false)}
            />
          </div>
        );
      case "application/pdf":
        return (
          <div className="w-full">
            <embed
              src={fileUrl}
              type="application/pdf"
              className="w-full h-[60vh] rounded"
              onError={() => setCanLoad(false)}
            />
          </div>
        );
      case "application/msword":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      case "text/csv":
        return (
          <div className="text-center p-4">
            <Text>
              This file type ({mimeType.split("/")[1].toUpperCase()}) cannot be
              previewed in the browser.
            </Text>
            <Button
              type="primary"
              href={fileUrl}
              download={doc.fileName || "document"}
              className="mt-4"
            >
              Download File
            </Button>
          </div>
        );
      default:
        return <Text>Unsupported file type</Text>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin />
      </div>
    );
  }

  const handleUploadSuccess = () => {
    refetch();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <Text strong className="text-lg">
          Profile Documents
        </Text>
        <Button
          type="primary"
          icon={<Upload size={16} />}
          onClick={() => setUploadVisible(true)}
        >
          Upload Documents
        </Button>
      </div>

      {profileDocuments.length === 0 ? (
        <Empty
          description="No documents uploaded"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button
            type="primary"
            icon={<Upload size={16} />}
            onClick={() => setUploadVisible(true)}
          >
            Upload Your First Document
          </Button>
        </Empty>
      ) : (
        <List
        dataSource={profileDocuments}
        renderItem={(doc) => (
          <List.Item
            className="hover:bg-[#fffef5] transition-colors cursor-pointer border-b-2 border-black"
            onClick={() => viewDocument(doc)}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <FileText size={20} className="text-blue-600" />
                <div>
                  <Text strong>{doc.title || "Unnamed Document"}</Text>
                  {doc.type && (
                    <div className="text-xs text-gray-500 mt-1">
                      Type: {capitalize(doc.type)}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Text type="secondary" className="text-sm">
                  {formatDate(doc.createdAt)}
                </Text>
                <Button
                  type="text"
                  icon={<Eye size={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    viewDocument(doc);
                  }}
                />
                <Button
                  type="text"
                  icon={<Download size={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadDocument(doc);
                  }}
                />
              </div>
            </div>
          </List.Item>
        )}
        />
      )}

      <ProfileDocumentUpload
        visible={uploadVisible}
        onClose={() => setUploadVisible(false)}
        onSuccess={handleUploadSuccess}
      />

      <Modal
        title={selectedDoc?.title || "View Document"}
        open={previewVisible}
        onCancel={() => {
          setPreviewVisible(false);
          setSelectedDoc(null);
        }}
        footer={[
          <Button
            key="download"
            type="primary"
            onClick={() => selectedDoc && downloadDocument(selectedDoc)}
          >
            Download File
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setPreviewVisible(false);
              setSelectedDoc(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedDoc && <DocumentPreview doc={selectedDoc} />}
      </Modal>
    </div>
  );
};

